import { useState } from "react";
import type { AnimatedImageProps } from "./types";
import { buildCloudinaryUrl } from "@/lib/cloudinaryUrl";

export function AnimatedImage({ image, artist, onClick }: AnimatedImageProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  const altText = image.altText ||
    (image.tags?.length ? `${image.tags.join(", ")} by ${artist.name}` : `Art by ${artist.name}`);

  const uploadDate = image.created_at
    ? new Date(image.created_at).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric"
      })
    : null;

  // IMPORTANT: Do NOT use `image.thumbnail` here — the upload pipeline creates
  // thumbnails as square 800x800 crops (portfolio-upload.mts `crop: 'fill'`),
  // but `image.width`/`image.height` reflect the ORIGINAL non-square dimensions.
  // Rendering a square thumbnail inside a container with the original aspect
  // ratio stretches the image. Use the full URL with a Cloudinary width
  // transform — the CDN downscales preserving the natural ratio, and `f_auto`
  // still negotiates WebP/AVIF at the edge.
  const displaySrc = buildCloudinaryUrl(image.url, { width: 800 }) || image.url;

  // Reserve space with aspect-ratio to prevent layout shift (fix for H-CLS-1).
  // Dimensions are present on GalleryImage when the backend populated them.
  const aspectRatio = image.width && image.height
    ? `${image.width} / ${image.height}`
    : undefined;

  if (hasError) {
    return (
      <div className="portfolio-item">
        <div
          className="w-full bg-[var(--color-bg-card)] flex items-center justify-center rounded-xl"
          style={{ aspectRatio: aspectRatio || "1 / 1" }}
        >
          <span className="text-[var(--color-text-muted)] text-sm">Failed to load</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className="portfolio-item group cursor-pointer"
      onClick={onClick}
    >
      {/* Image - displays at natural aspect ratio with pre-reserved box to prevent CLS */}
      <img
        src={displaySrc}
        alt={altText}
        loading="lazy"
        decoding="async"
        width={image.width}
        height={image.height}
        onLoad={() => setIsLoaded(true)}
        onError={() => setHasError(true)}
        className="w-full h-auto rounded-xl transition-all duration-300 group-hover:scale-[1.02]"
        style={{
          aspectRatio,
          opacity: isLoaded ? 1 : 0,
          transition: "opacity 0.3s ease"
        }}
      />

      {/* Overlay with artist info */}
      <div className="portfolio-item-overlay">
        <span className="portfolio-item-artist">{artist.name}</span>
        {uploadDate && (
          <span className="portfolio-item-date">{uploadDate}</span>
        )}
      </div>
    </div>
  );
}
